<script>
  import { onMount } from "svelte"
  import { push, querystring } from "svelte-spa-router"
  import ApplicationState from "../../../../../Stores/applicationStates"
  import { queryStringToJSON } from "../../../../../Script/queryString"
  import notify from "../../../../../Script/notify"
  import Navbar from "../../../../../Shared/Navbar/index.svelte"
  import Footer from "../../../../../Shared/footer.svelte"
  import Header from "../../Components/header.svelte"
  import LoadingScreen from "../../../SinglePublish/Components/loadingScreen.svelte"
  import BadgePublish, { updateBadgePublish } from "../../Stores/badge"
  import { datahashGeneration } from "../../Script/api"

  /**
   * @type {Boolean} - whether to disable next button or not
   */
  let showLoading = false
  /**
   * @type {String} - massage for loading screen
   */
  let msg
  /**
   * @type {Object} - object get from window location
   */
  let query = queryStringToJSON($querystring)
  /**
   * @type {Element} - document template shown element
   */
  let mainDiv
  /**
   * @type {Number} - max charater for metadata's value
   */
  let maxCharacters = 78
  /**
   * @typedef {Object} closeNotification
   * @property {Function} close - Closes the notification.
   */
  /**
   * @type {closeNotification}
   */
  let closeNotification = {
    close() {},
  }

  let title = ""

  let expireOn

  /**
   * fetch new document id details if the contition is satisfied
   */
  onMount(async () => {
    try {
      $BadgePublish.metadata.document["Issued for Domain"] = $BadgePublish.domain

      showLoading = true
      if (!$BadgePublish.documentTemplateId) push(`/publish/badge/doctemplate?documentId=${query.documentId}&badgeId=${query.badgeId}`)
      if (mainDiv) mainDiv.scrollIntoView()
    } catch (error) {
      const info = "PAGE: Badge Publish (Edit Badge Metadata)\nEVENT: onMount"
      notify.danger(error, info)
    } finally {
      showLoading = false
    }
  })

  /**
   * @description check max character function for metadata value
   */
  const checkMaxCharacters = async (e, parent, key) => {
    let inputValue
    if (e.target.value.length > maxCharacters) {
      closeNotification.close()
      //returning values only in range below max characters using slice method after a delay of 20ms
      inputValue = e.target.value.slice(0, maxCharacters + 1)
      if (parent == "options") title = inputValue
      else $BadgePublish.metadata[parent][key] = inputValue
      //Throw notification for exceeding max Characters
      closeNotification = notify.info(`Cannot enter more than 80 characters`)
    }
  }

  /**
   * @description update the metadata value based on user input
   */
  const updateValue = (e, parent, key) => {
    if (parent == "options") title = e.target.value.trim()
    else $BadgePublish.metadata[parent][key] = e.target.value.trim()
  }

  /**
   * @description before redirect next page check any metadata value fields are empty
   */
  const checkEmpty = () => {
    return new Promise((resolve, reject) => {
      try {
        let parent = Object.keys($BadgePublish.metadata)
        for (let i = 0; i < parent.length; i++) {
          let keys = Object.values($BadgePublish.metadata[parent[i]])
          for (let j = 0; j < keys.length; j++) {
            if (!keys[j]) {
              throw "Don't leave empty field"
            }
          }
        }
        resolve()
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * @description function for goto docPreview page
   */
  const nextPage = async () => {
    try {
      closeNotification.close()
      showLoading = true
      msg = "Updating metadata"

      await checkEmpty()

      $BadgePublish.metadata.document["Issued for Domain"] = $BadgePublish.domain

      let sampleObj = {
        metadata: JSON.stringify($BadgePublish.metadata),
        options: {},
      }

      if (title) sampleObj.options.title = title
      if (expireOn) sampleObj.options.expireOn = new Date(expireOn).toISOString()

      let apiData = await datahashGeneration($BadgePublish.documentId, sampleObj)

      $BadgePublish.options = JSON.parse(apiData.options)
      $BadgePublish.metadata = JSON.parse(apiData.metadata)
      $BadgePublish.dataHash = apiData.dataHash

      updateBadgePublish($BadgePublish)

      push(`/publish/badge/upload?documentId=${query.documentId}&badgeId=${query.badgeId}`)
    } catch (error) {
      // console.log(error)
      const info = "PAGE: Badge Publish (Edit Badge Metadata)\nEVENT: During validation of metadata fields before proceeding to publish"
      closeNotification = notify.danger(error, info)
    } finally {
      showLoading = false
    }
  }

  /**
   * Checks if a given date is expired.
   * @param {string} value - The date value to be validated.
   * @returns {boolean} True if the date is expired, otherwise false.
   */
  const isDateExpired = (value) => {
    let expirationDate = new Date(value)
    let currentDate = new Date()

    // Remove the time component from both dates
    expirationDate.setHours(0, 0, 0, 0)
    currentDate.setHours(0, 0, 0, 0)

    // Compare the dates
    if (expirationDate <= currentDate) {
      return true // Expiration date is in the past
    } else {
      return false // Expiration date is valid
    }
  }

  /**
   * @description function for check user given date doesn't older
   */
  const checkDate = async (e) => {
    closeNotification.close()
    try {
      if (isDateExpired(e.target.value)) throw "You've selected an older date. Please choose a future date as the expiration date."
    } catch (error) {
      expireOn = ""
      const info = "PAGE: Badge Publish (Edit Badge Metadata)\nEVENT: Expiry date validation on input change"
      closeNotification = notify.danger(error, info)
    }
  }
</script>

<svelte:window
  on:load={() => {
    push(`/publish/badge?documentId=${query.documentId}&badgeId=${query.badgeId}`)
  }}
/>

<svelte:head>
  <title>Publish Badge</title>
</svelte:head>

<div class="relative min-h-screen w-full overflow-hidden bg-[#f7f7f7] dark:bg-bodyblack">
  <Navbar />
  <div bind:this={mainDiv} class="mx-auto h-auto w-full max-w-[920px] px-6 pb-40 pt-6 lg:px-0">
    <Header showBackBtn={true} title="Edit Badge Metadata" bind:showLoading bind:msg redirectUrl="/publish/badge/doctemplate?documentId={query.documentId}&badgeId={query.badgeId}" />
    <div class="relative flex min-h-[530px] flex-col justify-between gap-6 rounded-md bg-white p-6 dark:bg-mdGray mobile:p-12">
      <div class="space-y-3">
        <p class="w-full select-none break-words text-justify text-sm text-gray-200 mobile:text-base sm:w-3/4 md:text-base">Please enter form fields to provided about the intended recipient,document and issuer of the document.In addition,title and expiry are optional details.</p>
        <div class="mobile:p-3">
          {#if $BadgePublish.metadata}
            <h1 class="select-none text-2xl font-bold text-black dark:text-white">Metadata</h1>
            {#each Object.keys($BadgePublish.metadata) as parent}
              <h3 class="my-3 select-none pl-3 text-base text-blue-500 mobile:pl-6 mobile:text-lg">
                <strong class="capitalize">{parent}</strong>
                Details
              </h3>
              <div class="space-y-3 pl-3">
                {#each Object.entries($BadgePublish.metadata[parent]) as [keys, values]}
                  <div class="flex w-full flex-col items-center justify-end gap-1 mobile:flex-row mobile:gap-6">
                    <div class="w-full select-none mobile:w-2/6 mobile:text-end">
                      <p class="text-sm capitalize text-gray-200 after:ml-0.5 after:text-red-500 mobile:font-medium {keys == 'Issued for Domain' ? '' : "after:content-['*']"}">{keys}</p>
                    </div>
                    <div class="w-full mobile:w-4/6">
                      <input readonly={keys == "Issued for Domain"} required on:blur={(e) => checkMaxCharacters(e, parent, keys)} on:keypress={(e) => checkMaxCharacters(e, parent, keys)} on:blur={(e) => updateValue(e, parent, keys)} bind:value={values} placeholder="Enter value" class="w-full rounded-md border border-[#a0a1a580] bg-transparent px-3 py-2 pl-5 text-sm text-gray-800 outline-none placeholder:font-normal placeholder:text-gray-200 invalid:animate-shake invalid:border-red-500 invalid:placeholder-red-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:invalid:border-red-500 focus:invalid:ring-red-500 dark:text-white sm:text-sm" />
                    </div>
                  </div>
                {/each}
              </div>
            {/each}
          {/if}
        </div>
        <div class="space-y-3 mobile:p-3">
          <h1 class="select-none text-2xl font-bold text-black dark:text-white">Options</h1>
          <div class=" flex w-full flex-col items-center justify-end gap-1 pl-3 mobile:flex-row mobile:gap-6">
            <div class="w-full select-none mobile:w-2/6 mobile:text-end">
              <p class="text-sm text-gray-200 mobile:font-medium">Title (Optional)</p>
            </div>
            <div class="w-full mobile:w-4/6">
              <input on:blur={(e) => checkMaxCharacters(e, "options", "title")} on:keypress={(e) => checkMaxCharacters(e, "options", "title")} on:blur={(e) => updateValue(e, "options", "title")} bind:value={title} placeholder="Enter title" class="w-full rounded-md border border-[#a0a1a580] bg-transparent px-3 py-2 pl-5 text-sm text-gray-800 outline-none placeholder:font-normal placeholder:text-gray-200 invalid:animate-shake invalid:border-red-500 invalid:placeholder-red-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:invalid:border-red-500 focus:invalid:ring-red-500 dark:text-white sm:text-sm" />
            </div>
          </div>
          <div class="flex w-full flex-col items-center justify-end gap-1 pl-3 mobile:flex-row mobile:gap-6">
            <div class="w-full select-none mobile:w-2/6 mobile:text-end">
              <p class="text-sm text-gray-200 mobile:font-medium">Expire On (Optional)</p>
            </div>
            <div class="relative w-full mobile:w-4/6">
              <input type="date" id="dateInput" bind:value={expireOn} on:change={checkDate} placeholder="Enter date" style="color-scheme: {$ApplicationState.darkMode ? 'dark' : 'light'} ;" class="absolute z-20 h-[37.6px] w-full rounded-md border border-[#a0a1a580] bg-transparent px-3 pl-5 text-sm text-gray-800 outline-none placeholder:font-normal placeholder:text-gray-200 invalid:animate-shake invalid:border-red-500 invalid:placeholder-red-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:invalid:border-red-500 focus:invalid:ring-red-500 dark:text-white sm:text-sm" />
              <div class="absolute right-3 top-2 z-10 text-gray-800 dark:text-slate-200">
                <i class="fa-solid fa-calendar-days" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex items-center justify-end gap-3">
        <button
          on:click={() => {
            $BadgePublish.documentTemplateId = ""
            push(`/publish/badge/doctemplate?badgeId=${query.badgeId}`)
          }}
          type="button"
          class="flex h-9 w-auto min-w-[36px] items-center justify-center rounded-md border border-[#a0a1a580] bg-yellow-500 px-2 text-sm font-medium text-white hover:bg-yellow-600 hover:text-white focus:outline-none active:bg-yellow-500 disabled:cursor-not-allowed disabled:bg-blue-300 lg:px-4"
        >
          <i class="fa-solid fa-rotate-left" />
          <span class="select-none md:block">&nbsp;&nbsp;Change Template&nbsp;</span>
        </button>
        <button on:click={nextPage} type="button" class="flex h-9 w-auto min-w-[36px] items-center justify-center rounded-md border border-[#a0a1a580] bg-blue-500 px-2 text-sm font-medium text-white hover:bg-blue-600 hover:text-white focus:outline-none active:bg-blue-500 md:w-32 lg:px-4">
          <span class="select-none md:block">&nbsp;Next&nbsp;&nbsp;</span>
          <i class="fa-solid fa-chevron-right" />
        </button>
      </div>
      {#if showLoading}
        <LoadingScreen {msg} />
      {/if}
    </div>
  </div>
  <Footer />
</div>

<style>
  input#dateInput {
    position: relative;
  }

  input[type="date"]::-webkit-calendar-picker-indicator {
    background: transparent;
    bottom: 0;
    cursor: pointer;
    height: auto;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: auto;
  }
</style>
